import { faLocationArrow } from '@fortawesome/free-solid-svg-icons/faLocationArrow';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FlexGrid, Form, Input, Loader, Text } from '@gasbuddy/react-components';
import { useMutation } from '@apollo/client';
import classnames from 'classnames/bind';
import querystring from 'querystring';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation as useRRLocation } from 'react-router-dom';
import packageJson from '../../../../package.json';
import useLocation from '../../../client/hooks/useLocation';
import useStations from '../../../client/hooks/useStations';
import useUserPreferences, { UPDATE_USER_PREFERENCES } from '../../../client/hooks/useUserPreferences';
import Legend from '../Legend';
import Navbar from '../Navbar';
import SplitScreen from '../SplitScreen';
import StationListItem from '../StationListItem';
import TrackerMap from '../TrackerMap';
import DefaultHelmet from '../DefaultHelmet';
import useFeatures, { DYNAMIC_MAP_FEATURE_ID } from '../../../client/hooks/useFeatures';
import styles from './MainPage.module.css';
import ErrorBoundary from '../ErrorBoundary';

const cx = classnames.bind(styles);

export const INITIAL_CENTER = { lat: 32.69783, lng: -80.91777 };

export default function MainPage() {
  const { t, i18n } = useTranslation(['feature', 'miscellaneous']);
  const history = useHistory();
  const location = useRRLocation();
  const queryParams = querystring.parse((location.search || '?').substring(1));
  const [mapDragged, setMapDragged] = useState(false);
  const [activeStationId, setActiveStationId] = useState(queryParams.station_id || null);
  const [query, setQuery] = useState(queryParams.q || '');
  const [trackerMap, setTrackerMap] = useState();

  // User preferences GraphQL hooks
  const { userPreferences: userPreferencesData, isLoading: preferencesLoading } = useUserPreferences();
  const [updateUserPreferences] = useMutation(UPDATE_USER_PREFERENCES, {
    errorPolicy: 'ignore',
  });

  // Check if user is logged in (simplified check)
  const isLoggedIn = Boolean(userPreferencesData);

  // Sync database preferences to localStorage for logged-in users
  useEffect(() => {
    if (isLoggedIn && !preferencesLoading && userPreferencesData?.language) {
      const userLanguagePreference = userPreferencesData.language;
      if (userLanguagePreference !== i18n.language) {
        // Update i18n language (this will also trigger localStorage update via event listener)
        i18n.changeLanguage(userLanguagePreference);

        // Explicitly update localStorage as backup
        try {
          localStorage.setItem('gb_preferred_locale', userLanguagePreference);
        } catch {
          // Ignore localStorage errors
        }
      }
    }
  }, [isLoggedIn, preferencesLoading, userPreferencesData, i18n]);

  // Language change handler
  const handleLocaleChange = useCallback((newLocale) => {
    // Update i18n language (also updates localStorage via the event listener)
    i18n.changeLanguage(newLocale);

    // Update database for logged-in users
    if (isLoggedIn) {
      updateUserPreferences({
        variables: {
          preferences: {
            language: newLocale,
          },
        },
      });
    }
  }, [i18n, isLoggedIn, updateUserPreferences]);

  let coordinates;

  // If the user supplied a lat/lng pair in the query string, those coordinates would become the result location
  if (queryParams.lat && queryParams.lng) {
    coordinates = {
      lat: parseFloat(queryParams.lat),
      lng: parseFloat(queryParams.lng),
    };
  }

  const [features = [], { isLoading: isFetching }] = useFeatures([DYNAMIC_MAP_FEATURE_ID]);
  const [geocodedLocation, { isLoading: isGeocoding }] = useLocation(!coordinates ? queryParams.q : undefined);
  const [stations, { isLoading: isSearching }] = useStations(coordinates || geocodedLocation);
  const isLoading = isFetching || isGeocoding || isSearching;
  const activeStation = useMemo(
    () => activeStationId && stations.find(s => s.id === activeStationId),
    [activeStationId, stations],
  );
  const [center, setCenter] = useState(INITIAL_CENTER);

  const dynamicMap = features.find(f => f.name === DYNAMIC_MAP_FEATURE_ID);

  if (dynamicMap) {
    const { payload: { location: { lat, lng } } } = dynamicMap;
    coordinates = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };
  }

  const handleMapLoad = useCallback((map) => {
    setTrackerMap(map);
  }, []);

  const searchMapLocation = useCallback(() => {
    const mapCenter = trackerMap.getCenter();
    const lat = mapCenter.lat();
    const lng = mapCenter.lng();

    queryParams.lat = lat;
    queryParams.lng = lng;
    delete queryParams.q;

    history.push({
      search: `?${querystring.stringify(queryParams)}`,
    });

    setActiveStationId(null);
    setMapDragged(false);
  }, [history, queryParams, trackerMap]);

  useEffect(() => {
    if (activeStation && center === INITIAL_CENTER) {
      setCenter({ lat: activeStation.latitude, lng: activeStation.longitude });
    }
  }, [activeStation, center]);

  // Attempt to use HTML5 Geolocation
  const geolocate = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.navigator.geolocation.getCurrentPosition((position) => {
        const { coords: userLocation } = position;

        if (queryParams.lat !== userLocation.latitude && queryParams.lng !== userLocation.longitude) {
          queryParams.lat = userLocation.latitude;
          queryParams.lng = userLocation.longitude;

          delete queryParams.q;

          history.push({
            search: `?${querystring.stringify(queryParams)}`,
          });

          setMapDragged(false);
        }
      }, () => {
        // TODO Handle error here
      });
    }
  }, [history, queryParams]);

  const handleMarkerShow = useCallback(({ key }) => {
    setActiveStationId(key);
  }, []);

  const handleMarkerHide = useCallback(({ key }) => {
    if (key === activeStationId) {
      setActiveStationId(null);
    }
  }, [activeStationId]);

  const stationItemClickHandlers = useMemo(() => (
    stations.map(station => () => { setActiveStationId(station.id); })
  ), [stations]);

  const handleMapDragged = useCallback(() => {
    setMapDragged(true);
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    queryParams.q = query;
    delete queryParams.station_id;
    delete queryParams.lat;
    delete queryParams.lng;

    history.push({
      search: `?${querystring.stringify(queryParams)}`,
    });
  }, [history, query, queryParams]);

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  // Update query parameters if local state has changed
  useEffect(() => {
    const needsUpdate = activeStationId && queryParams.station_id !== activeStationId.toString();
    const needsRemoval = !activeStationId && queryParams.station_id;

    if (needsUpdate) {
      queryParams.station_id = activeStationId;
    } else if (needsRemoval) {
      delete queryParams.station_id;
    }

    if (needsUpdate || needsRemoval) {
      history.push({
        search: `?${querystring.stringify(queryParams)}`,
      });
    }
  }, [activeStationId, history, queryParams]);

  // Update local state if query string changes
  useEffect(() => {
    setQuery(queryParams.q || '');
  }, [queryParams.q]);

  // Update local state if query string changes
  useEffect(() => {
    setActiveStationId(queryParams.station_id ? parseInt(queryParams.station_id, 10) : null);
  }, [queryParams.station_id]);

  // Situate the map so markers are all in view appropriately
  useEffect(() => {
    if (stations.length && trackerMap) {
      const bounds = new window.google.maps.LatLngBounds();
      stations.forEach((station) => {
        bounds.extend(new window.google.maps.LatLng(station.latitude, station.longitude));
      });
      trackerMap.fitBounds(bounds);
    }
  }, [stations, trackerMap]);

  const [googleLoaded, setGoogleLoaded] = useState(false);
  const checkForGoogle = useCallback(() => {
    if (typeof window !== 'undefined' && window.google) {
      setGoogleLoaded(true);
    }
  }, []);

  // Check if Google Maps is already loaded on mount (e.g. cached page)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.google) {
      setGoogleLoaded(true);
    }
  }, []);

  // Generate markers for map
  const markers = useMemo(() => (
    stations.map(station => ({
      key: station.id,
      showInfo: station.id === activeStationId,
      station,
    }))
  ), [activeStationId, stations]);

  const [supportsGeolocation, setSupportsGeolocation] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'geolocation' in window.navigator) {
      setSupportsGeolocation(true);
    }
  }, []);

  return (
    <ErrorBoundary>
      <div>
        <DefaultHelmet onChangeClientState={checkForGoogle} />
        <Navbar
          onLocaleChange={handleLocaleChange}
          isLoggedIn={isLoggedIn}
        >
          {mapDragged
            && (
              <Button onClick={searchMapLocation} primary className={cx('floatRight')}>
                <span className={cx('nonMobile')}>{t('redoSearchInThisArea', { ns: 'feature' })}</span>
                <span className={cx('mobile')}>{t('searchHere', { ns: 'feature' })}</span>
              </Button>
            )
          }
        </Navbar>
        <SplitScreen style={{ top: 60 }}>
          <SplitScreen.StaticPane>
            {googleLoaded && (
              <TrackerMap
                onMapLoad={handleMapLoad}
                center={center}
                markers={markers}
                onMarkerShow={handleMarkerShow}
                onMarkerHide={handleMarkerHide}
                onDragEnd={handleMapDragged}
              />
            )}
          </SplitScreen.StaticPane>
          <SplitScreen.SlidingPane>
            <Form action="/" method="POST" onSubmit={handleSubmit} role="form">
              <Input
                innerComponent={
                  supportsGeolocation
                    ? <FontAwesomeIcon icon={faLocationArrow} className={cx('locationIcon')} onClick={geolocate} />
                    : <FontAwesomeIcon icon={faSearch} className={cx('searchIcon')} />
                }
                id="locationSearchInput"
                label={t('searchForStations', { ns: 'feature' })}
                onChange={handleQueryChange}
                value={query}
                disabled={isLoading}
              />
            </Form>
            <Legend />
            <div className={cx('results')}>
              {isLoading ? (
                <Loader>
                  <Text>{t('loading', { ns: 'miscellaneous' })}</Text>
                </Loader>
              ) : (
                <FlexGrid>
                  {stations.map((station, index) => (
                    <FlexGrid.Column desktop={6} key={station.id}>
                      <StationListItem
                        id={station.id}
                        address={station.address}
                        name={station.name}
                        brandName={station.brand_name}
                        distance={station.distance}
                        icon={(station.brands[0] || {}).image_url}
                        phone={station.phone}
                        onClick={stationItemClickHandlers[index]}
                        fuels={station.fuels}
                        emergencyStatus={station.emergency_status}
                      />
                    </FlexGrid.Column>
                  ))}
                </FlexGrid>
              )}
              <div className={cx(stations.length ? 'versionAndCopyrightInfo' : 'fixedVersionAndCopyrightInfo')}>
                <Text as="small" suppressHydrationWarning>
                  v {packageJson.version} &copy; {new Date().getFullYear()} GasBuddy LLC
                </Text>
              </div>
            </div>
          </SplitScreen.SlidingPane>
        </SplitScreen>
      </div>
    </ErrorBoundary>
  );
}
